import { useEffect, useRef, useState } from "react";
import { AiOutlineLineChart } from "react-icons/ai";
import { FaUpload } from "react-icons/fa";
import { HiOutlineUserGroup } from "react-icons/hi";
import styled from "styled-components";
import { ReactComponent as InfoSvg } from "../../../assets/images/icons/info.svg";
import { ReactComponent as OrderSvg } from "../../../assets/images/icons/orders.svg";
import AdminActionsButton from "../../../components/button/AdminActionsButton";
import InputfieldWithDropDown from "../../../components/input/InputfieldWithDropDown";
import AdminSidebar from "../../../components/sidebar/AdminSidebar";
import { usePopup } from "../../../context/PopupContext";
import { orderService } from "../../../service/OrderService";
import { productService } from "../../../service/ProductService";
import { shippingService } from "../../../service/ShippingService";
import { videoService } from "../../../service/VideoService";
import colors from "../../../theme/color/ColorTheme";
import fontSize from "../../../theme/fonts/FontTheme";

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Nav = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 10;

  @media (max-width: 1024px) {
    position: relative;
    height: auto;
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  margin-left: 260px;
  width: calc(100% - 260px);

  @media (max-width: 1200px) {
    margin-left: 80px;
    width: calc(100% - 80px);
    padding: 15px;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
    padding: 10px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: white;
  border-radius: 12px;

  @media (max-width: 768px) {
    padding: 20px 15px;
    border-radius: 8px;
  }
`;

const TopStatsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IconWrapper = styled.div`
  background: #f5f6fa;
  display: flex;
  height: 40px;
  width: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 22px;

  svg {
    stroke-width: 1.6px;
  }
`;

const StatLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #555;
`;

const StatValue = styled.h3`
  font-size: 24px;
  margin: 10px 0 5px;
  color: #222;
`;
const Title = styled.span`
  font-size: 20px;
  font-weight: 500;
`;

const InfoTextSpan = styled.div`
  display: flex;
  margin: 10px 0px;
  align-items: center;
  gap: 4px;
  font-size: ${fontSize.xs};
`;
const VideoSection = styled.div`
  margin: 40px 0px;
  padding: 32px;
  background-color: ${colors.backgroundTintSecondary};
  border-radius: 8px;
  justify-content: center;
`;

const UploadBox = styled.label`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${colors.border};
  background-color: white;
  border-radius: 12px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s;
  width: 94%;
  &:hover {
    border-color: #4a90e2;
    background: #f9fbff;
  }

  input {
    display: none;
  }
`;

const UploadIcon = styled(FaUpload)`
  font-size: 32px;
  color: #4a90e2;
  margin-bottom: 10px;
`;

const VideoPreview = styled.video`
  width: fit-content;
  max-height: fit-content;
  border-radius: 12px;
  margin: auto;
  align-items: center;
  display: flex;
  margin-top: 15px;
  background: #000;
`;

const Actions = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: center;
  gap: 15px;
`;
const ShippingSection = styled.div`
  padding: 32px;
  background-color: ${colors.backgroundTintSecondary};
  border-radius: 8px;
  width: 50%;
`;
const Label = styled.label`
  font-size: 14px;
  display: block;
  font-weight: 500;
  margin-top: 24px;
  margin-bottom: 8px;
  color: #333;
`;
const FixedInputWrapper = styled.div`
  width: 400px;
  display: flex;
  gap: 10px;
  & > div {
    width: 100%;
  }

  input,
  select {
    width: 100%;
    box-sizing: border-box;
  }
`;

const LoadingText = styled.div`
  color: #4a90e2;
  font-weight: 500;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-weight: 500;
  margin-top: 10px;
`;

function AdminDashboardPage() {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [videoUrl, setVideoUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [shippingFee, setShippingFee] = useState(null);
  const [updatedShippingFee, setUpdatedShippingFee] = useState();
  const [reload, setReload] = useState();
  const [sales, setSales] = useState();
  const [newOrdersCount, setNewOrdersCount] = useState();
  const [ongoingOrdersCount, setOngoingOrdersCount] = useState();
  const [productCount, setProductCount] = useState();
  const { showPopup } = usePopup();

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("video/")) {
      setSelectedFile(file);
      setVideoUrl(URL.createObjectURL(file));
      setError("");
    } else {
      setError("Please upload a valid video file");
    }
  };

  const handleDelete = () => {
    setVideoUrl("");
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a video file first");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("video", selectedFile);
      videoService.saveVideo(
        formData,
        () => {
          showPopup("Video uploaded successfully");
        },
        {
          default: (message) => {
            alert("Error: " + message);
          },
        }
      );
    } catch (err) {
      setError(err.message || "Failed to upload video");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };
  const handleUpdateShippingFee = async () => {
    if (!updatedShippingFee || updatedShippingFee.trim() === "") {
      alert("Shipping fee not entered.");
      return;
    }
    shippingService.updateShippingFee(
      { fee: updatedShippingFee },
      () => {
        showPopup(
          "Updated Shipping Fee",
          "The change will now be visible across all products."
        );
        setReload(true);
      },
      {
        default: () => {
          alert("Error updating shipping fee");
        },
      }
    );
  };

  useEffect(() => {
    setReload(false);
    shippingService.getShippingFee(
      (fee) => {
        setShippingFee(fee.shippingFee);
      },
      {
        default: () => {
          setShippingFee(0);
        },
      }
    );
  }, [reload]);

  useEffect(() => {
    setReload(false);
    productService.getSales(
      (res) => {
        setSales(Math.round(res.sales.totalSales));
      },
      { default: () => setSales("Err") }
    );
  }, [reload]);

  useEffect(() => {
    setReload(false);
    productService.getProductCount(
      (res) => {
        setProductCount(Math.round(res.count));
      },
      { default: () => setProductCount("Err") }
    );
  }, [reload]);

  useEffect(() => {
    setReload(false);
    orderService.getNewOrderCount(
      (res) => {
        setNewOrdersCount(res);
      },
      { default: () => setNewOrdersCount("Err") }
    );
  }, [reload]);

  useEffect(() => {
    setReload(false);
    orderService.getOngoingOrderCount(
      (res) => {
        setOngoingOrdersCount(res);
      },
      { default: () => setOngoingOrdersCount("Err") }
    );
  }, [reload]);

  return (
    <Wrapper>
      <Nav>
        <AdminSidebar collapsed={window.innerWidth < 1200} />
      </Nav>
      <Content>
        <Container>
          <TopStatsWrapper>
            <StatCard>
              <StatHeader>
                <StatLabel>Today's Sales</StatLabel>
                <IconWrapper>रु</IconWrapper>
              </StatHeader>
              <StatValue>रु {sales && sales}</StatValue>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatLabel>New Orders</StatLabel>
                <IconWrapper>
                  <OrderSvg />
                </IconWrapper>
              </StatHeader>
              <StatValue>{newOrdersCount && newOrdersCount}</StatValue>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatLabel>Ongoing Orders</StatLabel>
                <IconWrapper>
                  <HiOutlineUserGroup />
                </IconWrapper>
              </StatHeader>
              <StatValue>{ongoingOrdersCount && ongoingOrdersCount}</StatValue>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatLabel>Product Count</StatLabel>
                <IconWrapper>
                  <AiOutlineLineChart />
                </IconWrapper>
              </StatHeader>
              <StatValue>{productCount && productCount}</StatValue>
            </StatCard>
          </TopStatsWrapper>

          <VideoSection>
            <Title>Upload Video</Title>
            <InfoTextSpan>
              <InfoSvg />
              <p>
                Any previously uploaded video will be automatically updated and
                replaced upon the upload of a new video.
              </p>
            </InfoTextSpan>
            {!videoUrl ? (
              <UploadBox>
                <UploadIcon />
                <span>Click or drag & drop to upload video</span>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
              </UploadBox>
            ) : (
              <>
                <VideoPreview src={videoUrl} controls />
                <Actions>
                  <AdminActionsButton
                    variant="primary"
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <LoadingText>Uploading...</LoadingText>
                    ) : (
                      "Upload Video"
                    )}
                  </AdminActionsButton>
                  <AdminActionsButton
                    variant="secondary"
                    onClick={handleDelete}
                    disabled={uploading}
                  >
                    Delete Video
                  </AdminActionsButton>
                </Actions>
              </>
            )}
            {error && <ErrorText>{error}</ErrorText>}
            <input
              type="file"
              accept="video/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </VideoSection>
          {shippingFee != null && (
            <ShippingSection>
              <Title>Current Shipping Fee: Rs.{shippingFee}</Title>
              <InfoTextSpan>
                <InfoSvg />
                <p>
                  This shipping fee will be applicable to all orders and
                  automatically applied upon purchase.
                </p>
              </InfoTextSpan>

              <Label>Update Shipping Fee</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="text"
                  placeholder="Enter shipping fee"
                  onChange={(e) => {
                    setUpdatedShippingFee(e.target.value);
                  }}
                />
                <AdminActionsButton
                  children={"Save"}
                  onClick={() => {
                    handleUpdateShippingFee();
                  }}
                />
              </FixedInputWrapper>
            </ShippingSection>
          )}
        </Container>
      </Content>
    </Wrapper>
  );
}

export default AdminDashboardPage;
